import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserDetail.css';

function UserDetail({ user, onClose, onUpdate, stats }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [superviseurAgents, setSuperviseurAgents] = useState([]);
  const [agentsDisponibles, setAgentsDisponibles] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');

  useEffect(() => {
    if (user.role === 'agent' || user.role === 'commercial') {
      fetchUserClients();
    } else if (user.role === 'superviseur') {
      fetchSuperviseurAgents();
      setLoading(false);
    } else if (user.role === 'superviseur_agent') {
      // Superviseur + Agent : charger les deux
      fetchUserClients();
      fetchSuperviseurAgents();
    } else {
      setLoading(false);
    }
  }, [user.id]);

  const fetchUserClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/clients', {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filtrer les clients assignés à cet utilisateur
      const userClients = response.data.filter(client => {
        if (user.role === 'agent' || user.role === 'superviseur_agent') {
          return client.agent_id === user.id;
        } else if (user.role === 'commercial') {
          return client.commercial_id === user.id;
        }
        return false;
      });

      setClients(userClients);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuperviseurAgents = async () => {
    setLoadingAgents(true);
    try {
      const token = localStorage.getItem('token');

      // Récupérer les agents assignés
      const agentsResponse = await axios.get(`/api/superviseurs/${user.id}/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuperviseurAgents(agentsResponse.data);

      // Récupérer les agents disponibles
      const disponiblesResponse = await axios.get(`/api/superviseurs/${user.id}/agents-disponibles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentsDisponibles(disponiblesResponse.data);
    } catch (error) {
      console.error('Erreur lors du chargement des agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleAssignAgent = async () => {
    if (!selectedAgent) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/superviseurs/${user.id}/agents`,
        { agent_id: selectedAgent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSelectedAgent('');
      fetchSuperviseurAgents();
    } catch (error) {
      console.error('Erreur lors de l\'assignation:', error);
      alert(error.response?.data?.error || 'Erreur lors de l\'assignation');
    }
  };

  const handleRemoveAgent = async (agentId) => {
    if (!window.confirm('Voulez-vous retirer cet agent du superviseur ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/superviseurs/${user.id}/agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchSuperviseurAgents();
    } catch (error) {
      console.error('Erreur lors du retrait:', error);
      alert(error.response?.data?.error || 'Erreur lors du retrait');
    }
  };

  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return `${parseFloat(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { text: 'Administrateur', class: 'badge-admin' },
      agent: { text: 'Agent Téléphonique', class: 'badge-agent' },
      commercial: { text: 'Commercial', class: 'badge-commercial' },
      superviseur: { text: 'Superviseur', class: 'badge-superviseur' },
      superviseur_agent: { text: 'Superviseur + Agent', class: 'badge-superviseur-agent' }
    };
    return badges[role] || { text: role, class: 'badge-default' };
  };

  const roleInfo = getRoleBadge(user.role);

  return (
    <div className="detail-overlay">
      <div className="detail-container">
        <div className="detail-header">
          <div>
            <h2>Fiche Utilisateur</h2>
            <p className="client-name">{user.nom}</p>
          </div>
          <div className="header-actions">
            <button onClick={() => onUpdate(user)} className="btn-edit">
              Modifier
            </button>
            <button onClick={onClose} className="btn-close">
              ✕
            </button>
          </div>
        </div>

        <div className="detail-content">
          {/* Informations générales */}
          <section className="detail-section">
            <h3>Informations générales</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Email</label>
                <p>{user.email}</p>
              </div>
              <div className="info-item">
                <label>Rôle</label>
                <p>
                  <span className={`role-badge ${roleInfo.class}`}>
                    {roleInfo.text}
                  </span>
                </p>
              </div>
              {(user.role === 'agent' || user.role === 'commercial' || user.role === 'superviseur_agent') && (
                <>
                  <div className="info-item">
                    <label>Taux de commission</label>
                    <p className="highlight">{parseFloat(user.taux_commission || 0).toFixed(2)}%</p>
                  </div>
                  <div className="info-item">
                    <label>Commission fixe</label>
                    <p className="highlight">{parseFloat(user.commission_fixe || 0).toFixed(2)}€</p>
                  </div>
                </>
              )}
              {(user.role === 'superviseur' || user.role === 'superviseur_agent') && (
                <div className="info-item">
                  <label>Commission superviseur par client payé</label>
                  <p className="highlight" style={{color: '#9b59b6'}}>{parseFloat(user.commission_superviseur || 0).toFixed(2)}€</p>
                </div>
              )}
              <div className="info-item">
                <label>Date de création</label>
                <p>{formatDate(user.created_at)}</p>
              </div>
            </div>
          </section>

          {/* Statistiques pour les agents, commerciaux et superviseurs_agents */}
          {(user.role === 'agent' || user.role === 'commercial' || user.role === 'superviseur_agent') && stats && (
            <section className="detail-section">
              <h3>Statistiques</h3>
              <div className="stats-grid">
                <div className="stat-box">
                  <label>CA Généré (Total BC)</label>
                  <p className="stat-value" style={{ color: '#667eea' }}>{formatCurrency(stats.ca_genere)}</p>
                </div>
                <div className="stat-box">
                  <label>CA Payé</label>
                  <p className="stat-value" style={{ color: '#2196f3' }}>{formatCurrency(stats.ca_paye)}</p>
                </div>
                <div className="stat-box">
                  <label>Commissions Totales</label>
                  <p className="stat-value" style={{ color: '#333' }}>{formatCurrency(stats.commissions_totales)}</p>
                </div>
                <div className="stat-box">
                  <label>Commissions Payées</label>
                  <p className="stat-value" style={{ color: '#2e7d32' }}>{formatCurrency(stats.commissions_payees)}</p>
                </div>
                <div className="stat-box">
                  <label>Commissions à Venir</label>
                  <p className="stat-value" style={{ color: '#f57c00' }}>{formatCurrency(stats.commissions_a_venir)}</p>
                </div>
                <div className="stat-box">
                  <label>Nombre de clients</label>
                  <p className="stat-value" style={{ color: '#667eea' }}>{clients.length}</p>
                </div>
              </div>
            </section>
          )}

          {/* Liste des clients assignés */}
          {(user.role === 'agent' || user.role === 'commercial' || user.role === 'superviseur_agent') && (
            <section className="detail-section">
              <h3>Clients assignés ({clients.length})</h3>
              {loading ? (
                <p>Chargement des clients...</p>
              ) : clients.length === 0 ? (
                <p className="no-data">Aucun client assigné</p>
              ) : (
                <div className="clients-list">
                  <table>
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Entreprise</th>
                        <th>Email</th>
                        <th>Téléphone</th>
                        <th>Montant BC</th>
                        <th>Payé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.map((client) => (
                        <tr key={client.id}>
                          <td>{client.nom}</td>
                          <td>{client.entreprise}</td>
                          <td>{client.email}</td>
                          <td>{client.telephone}</td>
                          <td>{formatCurrency(client.montant_bon_commande)}</td>
                          <td>
                            <span className={`status-badge ${client.paye ? 'status-yes' : 'status-no'}`}>
                              {client.paye ? '✓ Payé' : '✗ Non payé'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* Gestion des agents pour les superviseurs */}
          {(user.role === 'superviseur' || user.role === 'superviseur_agent') && (
            <section className="detail-section">
              <h3>Agents supervisés ({superviseurAgents.length})</h3>

              {/* Formulaire d'ajout d'agent */}
              <div className="add-agent-form" style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '20px',
                padding: '15px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ddd'
                  }}
                >
                  <option value="">-- Sélectionner un agent à ajouter --</option>
                  {agentsDisponibles.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.nom} ({agent.role === 'agent' ? 'Agent' : 'Commercial'}) - {agent.email}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssignAgent}
                  disabled={!selectedAgent}
                  style={{
                    padding: '10px 20px',
                    background: selectedAgent ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: selectedAgent ? 'pointer' : 'not-allowed',
                    fontWeight: 600
                  }}
                >
                  + Ajouter
                </button>
              </div>

              {loadingAgents ? (
                <p>Chargement des agents...</p>
              ) : superviseurAgents.length === 0 ? (
                <p className="no-data">Aucun agent assigné à ce superviseur</p>
              ) : (
                <div className="clients-list">
                  <table>
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Email</th>
                        <th>Rôle</th>
                        <th>Taux Commission</th>
                        <th>Commission Fixe</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {superviseurAgents.map((agent) => (
                        <tr key={agent.id}>
                          <td>{agent.nom}</td>
                          <td>{agent.email}</td>
                          <td>
                            <span className={`role-badge ${getRoleBadge(agent.role).class}`}>
                              {getRoleBadge(agent.role).text}
                            </span>
                          </td>
                          <td>{parseFloat(agent.taux_commission || 0).toFixed(2)}%</td>
                          <td>{parseFloat(agent.commission_fixe || 0).toFixed(2)}€</td>
                          <td>
                            <button
                              onClick={() => handleRemoveAgent(agent.id)}
                              style={{
                                padding: '6px 12px',
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              Retirer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{
                marginTop: '20px',
                padding: '15px',
                background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                borderRadius: '8px',
                borderLeft: '4px solid #9b59b6'
              }}>
                <p style={{ margin: 0, color: '#6b21a8', fontSize: '14px' }}>
                  <strong>Commission superviseur:</strong> {user.nom} recevra <strong>{parseFloat(user.commission_superviseur || 0).toFixed(2)}€</strong> pour chaque client de ses agents qui est totalement payé.
                </p>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserDetail;
